import React, { useState } from 'react'
import { CameraIcon, Edit3, Share2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import renderWithLinksAndMentions from './utils/renderWithLinksAndMentions'
import UserListModal from './models/UserListModal'

const ProfileHeader = ({
  username,
  name,
  bio,
  followerCount,
  followingCount,
  totalNotes,
  publicNotesCount,
  profilePic,
  isOwnProfile,
  loggedInUser,
  userId,
  showAlert,
  setIsEditProfileModelOpen,
  isFollowing,
  isFollowLoading,
  onFollowToggle,
  showPublicOnly, // boolean - owner only
  onTogglePublic, // function - owner only
  followerList,
  followingList
}) => {
  const imageAPI = process.env.REACT_APP_IMAGEAPI
  const navigate = useNavigate()
  const hostLink = process.env.REACT_APP_HOSTLINK

  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [modalType, setModalType] = useState(null)
  const [isUserListModal, setIsUserListModalOpen] = useState(false)
  const [userList, setUserList] = useState([])
  const [isUserListLoading, setIsUserListLoading] = useState(false)

  // Share profile: copy link to clipboard and attempt Web Share API
  const shareProfile = async () => {
    const shareUrl = `${hostLink}/user/${username}`
    const shareTitle = name || `@${username}`

    // Try copying the URL to clipboard first (useful fallback)
    try {
      await navigator.clipboard.writeText(shareUrl)
      if (showAlert) showAlert('Profile link copied to clipboard!', '#D4EDDA')
    } catch (err) {
      console.error('Failed to copy profile link to clipboard', err)
    }

    // If native share is available, open the share dialog
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${shareTitle} on Wryta`,
          text: `Check out ${shareTitle} on Wryta\n Link: `,
          url: shareUrl
        })
        if (showAlert) showAlert('Profile shared successfully!', '#D4EDDA')
      } catch (err) {
        // User may have dismissed share dialog — not an error to surface
        console.error('Web Share API error or dismissed', err)
      }
    }
  }

  const handleUserListOpen = async (type) => {
    setModalType(type)
    setIsUserListModalOpen(true)
    setIsUserListLoading(true)
    setUserList([])

    try {
      const endpoint = type === 'followers' ? 'followers' : 'following'
      const response = await fetch(`${hostLink}/api/user/${userId}/${endpoint}`, {
        headers: {
          'auth-token': localStorage.getItem('token') || ''
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch user list')
      }

      const data = await response.json()
      setUserList(type === 'followers' ? (data.followers || []) : (data.following || []))
    } catch (error) {
      console.error('Error fetching user list:', error)
      if (showAlert) {
        showAlert('Failed to load the list.', '#F8D7DA')
      }
    } finally {
      setIsUserListLoading(false)
    }
  }

  // Close modals when clicking outside
  const handleModalClose = () => {
    setModalType(null)
    setIsUserListModalOpen(false)
    setUserList([])
  }

  return (
    <>
      <div className='flex flex-col text-text-light dark:text-text-dark px-4  mx-auto pt-44 mb-10 max-w-2xl'>
        <div className='flex items-center space-x-8 mb-4'>
          <div className='left relative flex flex-col items-center'>
            <div className='relative'>
              <button
                type='button'
                onClick={() => setIsLightboxOpen(true)}
                className='focus:outline-none'
                aria-label='Open full image'
              >
                <div
                  className='relative overflow-hidden rounded-full border-2 border-gray-400'
                  style={{
                    width: 'clamp(80px, 24vw, 160px)',
                    height: 'clamp(80px, 24vw, 160px)'
                  }}
                >
                  <img
                    src={profilePic}
                    alt={username}
                    className='w-full h-full object-cover'
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.src = `${imageAPI}${encodeURIComponent(username)}`
                    }}
                  />
                </div>

              </button>

              {isOwnProfile && (
                <button
                  type='button'
                  onClick={() => navigate('/upload-image')}
                  // size is relative to viewport width but clamped between 32px and 40px
                  style={{ width: 'clamp(32px, 6vw, 36px)', height: 'clamp(32px, 6vw, 36px)' }}
                  className='absolute bottom-1 right-1 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-md border border-gray-400 hover:scale-105 transition-transform'
                  aria-label='Upload profile picture'
                >
                  <CameraIcon style={{ width: 'clamp(16px, 3.5vw, 24px)', height: 'clamp(16px, 3.5vw, 24px)' }} className='text-gray-700 dark:text-gray-200' />
                </button>
              )}
            </div>

            <div className='name mt-2 md:mt-0 text-center md:text-left font-medium text-xl'>
              {name}
            </div>
          </div>

          <div className='right flex-grow'>
            <div className='flex flex-col space-y-4 md:space-y-9 w-full md:w-auto'>
              <div className='flex justify-center items-center space-x-4'>
                <h2 className='text-xl font-normal'>{username}</h2>
              </div>

              <div className='flex justify-center items-center flex-wrap gap-y-2 md:flex-nowrap md:space-x-12 '>
                <div className='w-1/2 md:w-auto text-center flex flex-col'>
                  <span className='font-semibold'>{totalNotes}</span>
                  <span className='text-sm text-slate-500 dark:text-gray-300 ml-1'>notes</span>
                </div>
                <div className='w-1/2 md:w-auto text-center flex flex-col'>
                  <span className='font-semibold'>{publicNotesCount}</span>
                  <span className='text-sm text-slate-500 dark:text-gray-300 ml-1'>public</span>
                </div>
                <div
                  className='w-1/2 md:w-auto text-center cursor-pointer flex flex-col'
                  onClick={() => handleUserListOpen('followers')}
                >
                  <span className='font-semibold'>{followerCount}</span>
                  <span className='text-sm text-slate-500 dark:text-gray-300 ml-1'>followers</span>
                </div>
                <div
                  className='w-1/2 md:w-auto text-center cursor-pointer flex flex-col'
                  onClick={() => handleUserListOpen('following')}
                >
                  <span className='font-semibold'>{followingCount}</span>
                  <span className='text-sm text-slate-500 dark:text-gray-300 ml-1'>following</span>
                </div>
              </div>

            </div>
          </div>
        </div>

        <div className='bio mb-4'>
          {bio && (
            <div className='text-sm'>
              <p className='whitespace-pre-wrap'>{renderWithLinksAndMentions(bio)}</p>
            </div>
          )}
        </div>

        <div className='actionBtn w-full flex gap-5 items-center'>
          {isOwnProfile
            ? (
              <>
                <button
                  onClick={() => setIsEditProfileModelOpen(true)}
                  className='w-full px-8 py-2 bg-slate-200 hover:bg-slate-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-sm flex items-center justify-center space-x-2 rounded-full shadow-sm transition-transform duration-150'
                >
                  <span>Edit profile</span>
                  <Edit3 size={16} />
                </button>
              </>
              )
            : (
                loggedInUser && (
                  <button
                    onClick={onFollowToggle}
                    disabled={isFollowLoading}
                    className={`w-full px-8 py-2 rounded-full ${
                  isFollowing
                      ? 'bg-gray-200 hover:bg-gray-300 text-black dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-white'
                      : 'bg-blue-500 hover:bg-blue-600 text-white dark:bg-blue-600 dark:hover:bg-blue-700'
                  } text-base font-medium focus:outline-none transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isFollowLoading ? '...' : (isFollowing ? 'Unfollow' : 'Follow')}
                  </button>
                )
              )}
          <button
            type='button'
            onClick={shareProfile}
            className='share bg-blue-600 hover:bg-blue-700 p-2 rounded-full focus:outline-none'
            aria-label={`Share ${username} profile`}
          >
            <Share2 />
          </button>

        </div>
      </div>

      {/* Lightbox modal */}
      {isLightboxOpen && (
        <div
          className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-90 z-50'
          onClick={() => setIsLightboxOpen(false)}
        >
          <img
            src={profilePic}
            alt='Full screen'
            className='w-60 h-60 md:w-96 md:h-96 object-cover rounded-2xl'
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Followers/Following modals */}
      {modalType === 'followers' && isUserListModal && (
        <UserListModal
          title='Followers'
          users={userList}
          onClose={handleModalClose}
          isOpen={isUserListModal}
          isLoading={isUserListLoading}
        />
      )}
      {modalType === 'following' && isUserListModal && (
        <UserListModal
          title='Following'
          users={userList}
          onClose={handleModalClose}
          isOpen={isUserListModal}
          isLoading={isUserListLoading}
        />
      )}
    </>
  )
}

export default ProfileHeader
